import type { ProjectAmpel, ProjectResponse, SalesDocumentResponse } from '@proerp/shared';

/**
 * Reine Aggregations-Helfer fürs Dashboard (testbar ohne React, §10). Beträge
 * bleiben in Mikro-Einheiten als bigint (kein Float, CLAUDE.md §6). Wie der Rest
 * des Dashboards summieren wir währungsübergreifend und zeigen EUR an — die
 * Firma rechnet im EUR-Default; gemischte Währungen sind heute die Ausnahme.
 */

/** Rechnungsarten, die fakturierten Umsatz darstellen. */
const REVENUE_KINDS: ReadonlySet<SalesDocumentResponse['kind']> = new Set([
  'INVOICE',
  'ADVANCE_INVOICE',
  'FINAL_INVOICE',
]);

/** Nur ausgestellte (nicht Entwurf/storniert) Belege zählen als Umsatz. */
const REVENUE_STATUSES: ReadonlySet<SalesDocumentResponse['status']> = new Set(['SENT', 'PAID']);

/** Offene Angebote = noch nicht entschieden (weder angenommen noch abgelehnt). */
const OPEN_QUOTE_STATUSES: ReadonlySet<SalesDocumentResponse['status']> = new Set(['DRAFT', 'SENT']);

/** Projekt-Endzustände — aus „aktiv" ausgenommen. */
const CLOSED_PROJECT_STATUSES: ReadonlySet<ProjectResponse['status']> = new Set([
  'GESCHLOSSEN',
  'STORNIERT',
]);

/**
 * Fakturierter Netto-Umsatz eines Kalenderjahres in Mikro-Einheiten.
 * Schlussrechnungen verrechnen Anzahlungen (Glossar §12), enthalten also nur den
 * Restbetrag — Anzahlung + Schluss + Rechnung summieren somit ohne Doppelzählung.
 * Stornorechnungen (CREDIT_NOTE) werden abgezogen.
 */
export function revenueMicrosForYear(docs: SalesDocumentResponse[], year: number): bigint {
  return docs.reduce((sum, doc) => {
    if (!REVENUE_STATUSES.has(doc.status)) return sum;
    if (new Date(doc.documentDate).getFullYear() !== year) return sum;
    if (REVENUE_KINDS.has(doc.kind)) return sum + BigInt(doc.net.amount);
    if (doc.kind === 'CREDIT_NOTE') return sum - BigInt(doc.net.amount);
    return sum;
  }, 0n);
}

export interface MonthlyRevenuePoint {
  /** ISO-Monat (JJJJ-MM), eindeutiger Schlüssel der X-Achse. */
  month: string;
  /** Anzeige-Label, z. B. „Jan." (de-DE, kurz). */
  label: string;
  /** Netto-Umsatz in Euro — Number NUR für die Chart-Darstellung (recharts);
      die Summierung selbst läuft in bigint-Mikro-Einheiten (CLAUDE.md §6). */
  euros: number;
}

const MONTH_LABEL = new Intl.DateTimeFormat('de-DE', { month: 'short' });

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Fakturierter Netto-Umsatz je Monat für die letzten `months` Monate
 * (inkl. laufendem Monat) — gleiche Zähl-Regeln wie `revenueMicrosForYear`
 * (nur SENT/PAID, Schluss-/Anzahlungslogik ohne Doppelzählung, Storno negativ).
 */
export function monthlyRevenueSeries(
  docs: SalesDocumentResponse[],
  months: number,
  now: Date = new Date(),
): MonthlyRevenuePoint[] {
  const buckets = new Map<string, bigint>();
  const order: { month: string; label: string }[] = [];
  for (let offset = months - 1; offset >= 0; offset--) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const key = monthKey(date);
    order.push({ month: key, label: MONTH_LABEL.format(date) });
    buckets.set(key, 0n);
  }

  for (const doc of docs) {
    if (!REVENUE_STATUSES.has(doc.status)) continue;
    const key = monthKey(new Date(doc.documentDate));
    const current = buckets.get(key);
    if (current === undefined) continue;
    if (REVENUE_KINDS.has(doc.kind)) buckets.set(key, current + BigInt(doc.net.amount));
    else if (doc.kind === 'CREDIT_NOTE') buckets.set(key, current - BigInt(doc.net.amount));
  }

  return order.map(({ month, label }) => ({
    month,
    label,
    euros: Number((buckets.get(month) ?? 0n) / 10_000n) / 100,
  }));
}

export interface OpenQuotesSummary {
  count: number;
  /** Summe der Brutto-Beträge offener Angebote (Mikro-Einheiten). */
  totalGrossMicros: bigint;
}

/** Offene Angebote (Entwurf/versendet) mit Anzahl und Brutto-Volumen. */
export function summarizeOpenQuotes(docs: SalesDocumentResponse[]): OpenQuotesSummary {
  const quotes = docs.filter((doc) => doc.kind === 'QUOTE' && OPEN_QUOTE_STATUSES.has(doc.status));
  const totalGrossMicros = quotes.reduce((sum, doc) => sum + BigInt(doc.gross.amount), 0n);
  return { count: quotes.length, totalGrossMicros };
}

/** Laufende Projekte (nicht geschlossen/storniert). */
export function isActiveProject(project: ProjectResponse): boolean {
  return !CLOSED_PROJECT_STATUSES.has(project.status);
}

export type AmpelBreakdown = Record<ProjectAmpel, number>;

/** Ampel-Verteilung über die aktiven Projekte. */
export function ampelBreakdown(projects: ProjectResponse[]): AmpelBreakdown {
  const breakdown: AmpelBreakdown = { GRUEN: 0, GELB: 0, ROT: 0 };
  for (const project of projects) {
    if (isActiveProject(project)) breakdown[project.ampel] += 1;
  }
  return breakdown;
}

/** ROT vor GELB; innerhalb gleicher Ampel das größere Auftragsvolumen zuerst. */
const AMPEL_PRIORITY: Record<ProjectAmpel, number> = { ROT: 0, GELB: 1, GRUEN: 2 };

function orderValueMicros(project: ProjectResponse): bigint {
  return project.orderValue ? BigInt(project.orderValue.amount) : 0n;
}

/**
 * Aktive Projekte mit Handlungsbedarf (Ampel ≠ Grün), sortiert nach Dringlichkeit
 * (ROT zuerst) und Auftragsvolumen — die „auf einen Blick"-Liste fürs Dashboard.
 */
export function criticalProjects(projects: ProjectResponse[]): ProjectResponse[] {
  return projects
    .filter((project) => isActiveProject(project) && project.ampel !== 'GRUEN')
    .sort((a, b) => {
      const byAmpel = AMPEL_PRIORITY[a.ampel] - AMPEL_PRIORITY[b.ampel];
      if (byAmpel !== 0) return byAmpel;
      const diff = orderValueMicros(b) - orderValueMicros(a);
      return diff > 0n ? 1 : diff < 0n ? -1 : 0;
    });
}
